import BaseNode from "./BaseNode";
import * as HoverCard from "./StepHoverCard";
import { InputStep, State } from "app/lib/pipeline";
import { Node, NodeProps } from "@xyflow/react";
import { WaitIcon } from "./WaitStepNode";
import { useMostRecentJob } from "app/components/build/Show/lib/BuildContext";
import Button from "app/components/shared/Button";
import { ComponentProps, forwardRef } from "react";
import { isInputJobUnblocked, InputJob } from "app/components/build/Show/lib/types";
import classNames from "classnames";
import Icon from "app/components/shared/Icon";
import UserAvatar from "app/components/shared/UserAvatar";
import FriendlyTime from "app/components/shared/FriendlyTime";
import { twMerge } from "tailwind-merge";
import Emojify from "app/components/shared/Emojify";

type UnblockButtonProps = ComponentProps<typeof Button> & { job: InputJob };

export const UnblockButton = forwardRef<HTMLAnchorElement, UnblockButtonProps>(
  ({ job, children, className, ...props }, ref) => {
    return (
      <a
        {...props}
        ref={ref}
        href={job.unblockDialogPath}
        data-turbo={true}
        data-turbo-frame="unblock_dialog"
        className={twMerge("no-underline text-inherit hover:text-inherit", className)}
      >
        {children}
      </a>
    );
  },
);

export const isInputNodeDisabled = (job: InputJob) => {
  return job.state === "blocked_failed" || job.state === "broken" || job.state === "canceled";
};

type InputStepNode = Node<InputStep, "input">;

export default function InputStepNode(props: NodeProps<InputStepNode>) {
  const job = useMostRecentJob<InputJob>(props.id);
  const node = (
    <div>
      {/* icon and border colour based off of step state. */}
      <BaseNode
        {...props}
        hideStateIcon={props.data.state !== State.Finished}
        label={<Emojify text={props.data.label || "Continue"} />}
        icon={
          <>
            <WaitIcon
              passed={job ? isInputJobUnblocked(job) : props.data.state === State.Finished}
            />
            {props.data.state !== State.Finished && (
              <span
                className={twMerge(
                  classNames(
                    "flex bg-purple-600 absolute top-0 bottom-0 left-full -translate-x-full rounded-r-xl text-white w-10 justify-center items-center",
                    {
                      "bg-slate-500": job?.state === "blocked_failed" || job?.state === "broken",
                    },
                  ),
                )}
              >
                <Icon icon="arrow-right" className="h-5 w-5" />
              </span>
            )}
          </>
        }
        disabled={job ? isInputNodeDisabled(job) : false}
        className={classNames({
          "opacity-100": props.data.state !== State.Ignored && job?.state !== "blocked_failed",
          "border-purple-600 border-4 pr-12":
            job && !isInputJobUnblocked(job) && !isInputNodeDisabled(job),
          "text-slate-500 border-2 pr-12":
            job?.state === "blocked_failed" || job?.state === "broken",
        })}
      />
    </div>
  );

  const renderNode = (job) => {
    // Include a null check in case the node is being rendered outside of a build context
    // and no job is available.
    if (job === null || isInputJobUnblocked(job) || isInputNodeDisabled(job)) {
      return node;
    }

    return (
      <UnblockButton job={job} className="unstyled-button">
        {node}
      </UnblockButton>
    );
  };

  return (
    <HoverCard.Root>
      <HoverCard.Portal container={document.getElementById("canvas")}>
        {job && props.data.state && isInputJobUnblocked(job) && (
          <HoverCard.Content
            state={props.data.state}
            outcome={props.data.outcome}
            missingDependencies={props.data.missingDependencies}
            collisionBoundary={document.getElementById("canvas")}
            collisionPadding={10}
          >
            <div className="space-y-3">
              <div className="flex flex-row items-center gap-2 w-full">
                <UserAvatar
                  className="h-10 w-10"
                  user={{
                    name: job.unblockedBy.name,
                    avatar: { url: job.unblockedBy.avatarUrl },
                  }}
                />
                <div className="flex flex-col flex-nowrap flex-1">
                  <div className="font-medium">{job.unblockedBy.name}</div>
                  <div className="dark-gray">
                    Unblocked <FriendlyTime value={job.unblockedAt} capitalized={false} />
                  </div>
                </div>
              </div>
            </div>
          </HoverCard.Content>
        )}
      </HoverCard.Portal>

      <HoverCard.Trigger asChild={true}>{renderNode(job)}</HoverCard.Trigger>
    </HoverCard.Root>
  );
}
